import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import { Shell } from "@/components/layout/Shell"
import { useAlarmStream } from "@/features/alarms/hooks/useAlarmStream"
import { dashboardPath, panelOfRoute } from "@/features/auth/panel-routes"
import { usePanelStore } from "@/features/auth/panel-store"
import { useAuthStore } from "@/features/auth/store"

/**
 * Auth gate + Shell.
 *  - Token yoksa /login.
 *  - User'ın panels listesine göre yönlendirme:
 *      0 panel  → forced logout + /login
 *      1 panel  → otomatik aktif panel set, sayfa devam
 *      2 panel + aktif panel yok → /select-panel
 *  - Bulunulan rota başka bir panele aitse aktif panele redirect.
 */
export const Route = createFileRoute("/_authed")({
  beforeLoad: ({ location }) => {
    const auth = useAuthStore.getState()
    if (!auth.isAuthenticated()) {
      throw redirect({ to: "/login" })
    }

    const panels = auth.user?.panels ?? []
    const path = location.pathname

    // Hiç panel yoksa → temizle, login'e
    if (panels.length === 0) {
      auth.clear()
      usePanelStore.getState().clear()
      throw redirect({ to: "/login" })
    }

    const panelStore = usePanelStore.getState()
    let active = panelStore.active

    // Tek panel → otomatik set
    const [first] = panels
    if (!active && panels.length === 1 && first) {
      panelStore.setActive(first)
      active = first
    }

    // Çift panel + aktif panel yok → seçim ekranı
    if (!active && panels.length > 1) {
      throw redirect({ to: "/select-panel" })
    }

    // Aktif panel set; bulunulan path'in panel sahibi farklıysa redirect
    if (active) {
      const owner = panelOfRoute(path)
      if (owner && owner !== active) {
        // Kullanıcı bu panele yetkili mi? Değilse aktif panele döndür.
        if (!panels.includes(owner)) {
          throw redirect({ to: dashboardPath(active) })
        }
        // Yetkili ama farklı panele giriyor → aktif paneli swap et
        panelStore.setActive(owner)
      }
    }
  },
  component: AuthedLayout,
})

function AuthedLayout() {
  // Auth guard geçtikten sonra alarm stream başlar; rota değiştikçe unmount olmaz.
  useAlarmStream()
  return (
    <Shell>
      <Outlet />
    </Shell>
  )
}
